import * as yup from 'yup';
import { riskBulkDraftSchema } from './schema.ts';

export type RiskDraftTableField = 'clientRiskId' | 'riskDescription' | 'riskLevel' | 'riskCategory';

export type RiskDraftValidationRow = {
  id: string;
  clientRiskId?: string | null;
  riskDescription?: string | null;
  riskLevel?: string | null;
  riskCategory?: string | null;
  validation_errors?: Array<{ column: string; reason?: string; message?: string }>;
};

function errorText(e: { reason?: string; message?: string }): string {
  return String(e.reason ?? e.message ?? '').trim();
}

export function normalizeRiskColumnLabel(column: string): string {
  return String(column || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function riskColumnMapsToField(column: string, field: RiskDraftTableField): boolean {
  const n = normalizeRiskColumnLabel(column);
  switch (field) {
    case 'clientRiskId':
      return n === 'clientriskid' || n === 'client_risk_id' || (n.includes('client') && n.includes('risk') && n.includes('id'));
    case 'riskDescription':
      return (
        (n.includes('risk') && n.includes('description')) || n === 'riskdescription' || n === 'description'
      );
    case 'riskLevel':
      return (n.includes('risk') && n.includes('level')) || n === 'risklevel';
    case 'riskCategory':
      return (n.includes('risk') && n.includes('category')) || n === 'riskcategory';
    default:
      return false;
  }
}

export function reasonsForRiskBackendField(
  errors: RiskDraftValidationRow['validation_errors'],
  field: RiskDraftTableField
): string[] {
  if (!errors?.length) return [];
  return errors
    .filter((e) => riskColumnMapsToField(String(e.column), field))
    .map((e) => errorText(e))
    .filter(Boolean);
}

export type RiskDraftRowMergeOptions = {
  showBackendErrors: boolean;
  applyFrontendValidation: boolean;
};

/**
 * Bulk draft fields validated live via {@link riskBulkDraftSchema} (same as manual create).
 * Description uses Yup only in the review table so stale upload errors are not replayed.
 */
const FRONTEND_ONLY_RISK_DRAFT_FIELDS: RiskDraftTableField[] = ['riskDescription'];

function looksLikeRequiredOrMissingReason(reason: string): boolean {
  const r = reason.toLowerCase();
  return (
    r.includes('required') ||
    r.includes('missing') ||
    r.includes('empty') ||
    r.includes('blank') ||
    r.includes('not provided')
  );
}

function looksLikeTooLongReason(reason: string): boolean {
  const r = reason.toLowerCase();
  return r.includes('too long') || (r.includes('maximum') && r.includes('character'));
}

function isRiskBackendCellErrorSupersededByCurrentValues(
  err: { column: string; reason?: string; message?: string },
  row: RiskDraftValidationRow,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown,
  opts: RiskDraftRowMergeOptions
): boolean {
  const reason = errorText(err);
  if (!reason) return false;
  const col = String(err.column || '');

  if (riskColumnMapsToField(col, 'riskDescription')) {
    const frontendErrors = validateRiskDraftRowFrontendSync(row, getFieldValue).riskDescription;
    return !frontendErrors?.length;
  }

  if (looksLikeTooLongReason(reason) && riskColumnMapsToField(col, 'clientRiskId')) {
    return String(getFieldValue(row, 'clientRiskId') ?? '').trim().length <= 50;
  }

  if (!looksLikeRequiredOrMissingReason(reason)) return false;

  if (riskColumnMapsToField(col, 'clientRiskId')) {
    return String(getFieldValue(row, 'clientRiskId') ?? '').trim().length > 0;
  }
  if (riskColumnMapsToField(col, 'riskDescription')) {
    return String(getFieldValue(row, 'riskDescription') ?? '').trim().length > 0;
  }
  if (riskColumnMapsToField(col, 'riskLevel')) {
    return String(getFieldValue(row, 'riskLevel') ?? '').trim().length > 0;
  }
  if (riskColumnMapsToField(col, 'riskCategory')) {
    return String(getFieldValue(row, 'riskCategory') ?? '').trim().length > 0;
  }
  return false;
}

export function effectiveRiskBackendErrorsForRow(
  row: RiskDraftValidationRow,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown,
  opts: RiskDraftRowMergeOptions
): NonNullable<RiskDraftValidationRow['validation_errors']> {
  const raw = Array.isArray(row.validation_errors) ? row.validation_errors : [];
  if (!opts.showBackendErrors) return [];
  return raw.filter((e) => !isRiskBackendCellErrorSupersededByCurrentValues(e, row, getFieldValue, opts));
}

export function orphanRiskBackendReasons(errors: RiskDraftValidationRow['validation_errors']): string[] {
  if (!errors?.length) return [];
  const fields: RiskDraftTableField[] = ['clientRiskId', 'riskDescription', 'riskLevel', 'riskCategory'];
  return errors
    .filter((e) => !fields.some((f) => riskColumnMapsToField(String(e.column), f)))
    .map((e) => {
      const r = errorText(e);
      const c = String(e.column || '').trim();
      if (c && r) return `${c}: ${r}`;
      return r || c;
    })
    .filter(Boolean);
}

export function stripRiskBackendErrorsForMappedField(
  errors: RiskDraftValidationRow['validation_errors'] | undefined,
  field: RiskDraftTableField
): Array<{ column: string; reason?: string; message?: string }> {
  if (!errors?.length) return [];
  return errors.filter((e) => !riskColumnMapsToField(String(e.column), field));
}

export function rowToRiskFormShape(
  row: RiskDraftValidationRow,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown
): {
  clientRiskId: string;
  riskDescription: string;
  riskLevel: unknown;
  riskCategory: string;
  classification: 'business' | 'itgc';
  itgcDomainId: string;
  processAssociations: unknown[];
} {
  return {
    clientRiskId: String(getFieldValue(row, 'clientRiskId') ?? '').trim(),
    riskDescription: String(getFieldValue(row, 'riskDescription') ?? '').trim(),
    riskLevel: getFieldValue(row, 'riskLevel'),
    riskCategory: String(getFieldValue(row, 'riskCategory') ?? '').trim(),
    classification: 'business',
    itgcDomainId: '',
    processAssociations: [],
  };
}

export type RiskFrontendFieldErrors = Partial<Record<RiskDraftTableField, string[]>>;

export function validateRiskDraftRowFrontendSync(
  row: RiskDraftValidationRow,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown
): RiskFrontendFieldErrors {
  const shape = rowToRiskFormShape(row, getFieldValue);
  const out: RiskFrontendFieldErrors = {};
  try {
    riskBulkDraftSchema.validateSync(shape, { abortEarly: false });
  } catch (e: unknown) {
    if (e instanceof yup.ValidationError) {
      const inner = e.inner && e.inner.length > 0 ? e.inner : [e];
      for (const err of inner) {
        const path = err.path as RiskDraftTableField | undefined;
        if (!path) continue;
        if (path === 'processAssociations') continue;
        if (out[path]?.length) continue;
        out[path] = [String(err.message || 'Invalid value')];
      }
    }
  }
  return out;
}

export function mergedRiskFieldReasons(
  row: RiskDraftValidationRow,
  field: RiskDraftTableField,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown,
  options: RiskDraftRowMergeOptions
): string[] {
  const backendList = effectiveRiskBackendErrorsForRow(row, getFieldValue, options);

  if (
    options.applyFrontendValidation &&
    FRONTEND_ONLY_RISK_DRAFT_FIELDS.includes(field)
  ) {
    return validateRiskDraftRowFrontendSync(row, getFieldValue)[field] || [];
  }

  const backendField = options.showBackendErrors ? reasonsForRiskBackendField(backendList, field) : [];
  const orphans =
    field === 'riskDescription' && options.showBackendErrors ? orphanRiskBackendReasons(backendList) : [];
  const backendCoversField =
    backendField.length > 0 || (field === 'riskDescription' && orphans.length > 0);
  const frontend =
    options.applyFrontendValidation && !backendCoversField
      ? validateRiskDraftRowFrontendSync(row, getFieldValue)[field] || []
      : [];

  if (field === 'riskDescription') {
    return [...backendField, ...orphans, ...frontend];
  }
  return [...backendField, ...frontend];
}

export function riskDraftRowHasAnyMergedError(
  row: RiskDraftValidationRow,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown,
  options: RiskDraftRowMergeOptions
): boolean {
  const fields: RiskDraftTableField[] = ['clientRiskId', 'riskDescription', 'riskLevel', 'riskCategory'];
  return fields.some((f) => mergedRiskFieldReasons(row, f, getFieldValue, options).length > 0);
}

export function selectedRiskDraftRowsHaveMergedErrors(
  rows: RiskDraftValidationRow[],
  selectedIds: Set<string>,
  getFieldValue: (row: RiskDraftValidationRow, field: RiskDraftTableField) => unknown,
  options: RiskDraftRowMergeOptions
): boolean {
  for (const row of rows) {
    if (!selectedIds.has(row.id)) continue;
    if (riskDraftRowHasAnyMergedError(row, getFieldValue, options)) return true;
  }
  return false;
}
